import { useEffect, useRef } from "react";

import { resolveBuildCommitSha } from "../buildConfig/sentryBuildConfig";
import { toastManager } from "../components/ui/toast";
import {
  STALE_BUILD_DESCRIPTOR_PATH,
  readServerBuildCommit,
  shouldPromptStaleBuildReload,
} from "./staleBuildWatchdog.logic";

/**
 * A tab left open across deploys re-checks roughly four times an hour; the
 * visibility listener catches the common case (user returns to an old tab)
 * immediately, so the interval is a backstop, not the primary signal.
 */
const STALE_BUILD_CHECK_INTERVAL_MS = 15 * 60 * 1000;

/** Let boot traffic settle before the first background check. */
const STALE_BUILD_FIRST_CHECK_DELAY_MS = 60 * 1000;

/**
 * Watches for the served app being newer than the running bundle and offers a
 * reload. Cache headers keep NEW page loads current; this is the only recovery
 * lane for tabs that are already open when a deploy lands. Without it, a
 * long-lived tab keeps every already-fixed bug alive until the user happens to
 * reload, which is how the retired cloud-thread blocking overlay outlived its
 * fix by a week (FOR-36252).
 */
export function useStaleBuildWatchdog(): void {
  const promptedRef = useRef(false);

  useEffect(() => {
    const clientCommit = resolveBuildCommitSha(
      import.meta.env as Record<string, string | undefined>,
    );
    // An unidentified bundle (desktop, dev) can never be proven stale.
    if (clientCommit === "") return;

    let disposed = false;

    const check = async () => {
      if (disposed || promptedRef.current) return;
      let serverCommit: string | undefined;
      try {
        const response = await fetch(STALE_BUILD_DESCRIPTOR_PATH, {
          cache: "no-store",
          credentials: "same-origin",
        });
        if (!response.ok) return;
        serverCommit = readServerBuildCommit(await response.json());
      } catch {
        // Offline or mid-deploy; the next tick or visibility change retries.
        return;
      }
      if (disposed || promptedRef.current) return;
      if (!shouldPromptStaleBuildReload({ clientCommit, serverCommit })) return;
      promptedRef.current = true;
      toastManager.add({
        type: "info",
        title: "Forge was updated",
        description: "Reload to get the latest version.",
        // Stays until acted on: an auto-dismissing prompt leaves the stale
        // bundle running, which is the situation this exists to end.
        timeout: 0,
        actionProps: {
          children: "Reload",
          onClick: () => {
            window.location.reload();
          },
        },
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void check();
    };

    const firstCheck = window.setTimeout(() => void check(), STALE_BUILD_FIRST_CHECK_DELAY_MS);
    const interval = window.setInterval(() => void check(), STALE_BUILD_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      window.clearTimeout(firstCheck);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
}
